import { createRouter, createWebHistory } from 'vue-router'
import { useVfmModals } from 'hooks/modals.js'
import { useAuth } from 'hooks/useAuth.js'
import { markBonusSportModalAsHandled, useBonusSportModal } from 'src/hooks/useBonusSportModal'
import { useSystemStore } from 'src/stores/system'
import { useUserAuthStore } from 'src/stores/user/auth'
import { BASE_URL } from 'src/common/config'
import { clearQueryAfterAuth, getNewRouteByLang, notifyIfChunkError } from 'src/common/functions'
import { UZBEK_CYRILLIC_ENABLED } from 'src/common/utils/locale'
import { postMessageFromWebviewToApp } from 'src/common/webview'
import { webviewRouteNames } from 'src/router/webview-routes'
import routes from './routes'
import { getConfig } from '@/configs/index.js'

let alreadyBeforeEach = false
let skipSportsBonusModalOnce = false

export default function () {
  const routesList = routes()

  const router = createRouter({
    scrollBehavior: (to, from, savedPosition) => {
      const routeNames = ['Home', 'Casino', 'GamesBySubGroup', 'GamesByGroup', 'BonusGames', 'GamesByManufacturer']

      if (from.name === 'Promotion' && to.name === 'GamesBySubGroup') {
        return { left: 0, top: 0, behavior: 'auto' }
      }

      if (routeNames.includes(to.name) && routeNames.includes(from.name) && savedPosition) {
        return { ...savedPosition, behavior: 'smooth' }
      }
      return { left: 0, top: 0, behavior: 'smooth' }
    },
    routes: routesList,
    base: BASE_URL,
    history: createWebHistory(BASE_URL),
    linkExactActiveClass: 'exact-active',
  })

  router.beforeEach((to, from, next) => {
    let resolved = false
    const guardedNext = (...args) => {
      resolved = true
      next(...args)
    }

    const middlewares = [
      () => middlewareWebviewBypass(to, from, guardedNext),
      () => middlewareLangRedirect(to, from, guardedNext, router),
      () => middlewarePathQueryRedirect(to, from, guardedNext, router),
      () => middlewareMirrorLocaleCleanup(to, from, guardedNext),
      () => middlewareLiveChat(to),
      () => middlewarePostPaymentRedirect(to, from, guardedNext, router),
      () => middlewareSportsBonusModal(to, from, guardedNext, router),
      () => middlewareAuthGuard(to, from, guardedNext, router),
    ]

    for (const middleware of middlewares) {
      middleware()
      if (resolved) {
        return
      }
    }
  })

  router.onError((e) => {
    notifyIfChunkError(e)
  })

  router.afterEach(() => {
    window.PopMechanic && window.PopMechanic.update && window.PopMechanic.update()
  })

  return router
}

function middlewareWebviewBypass(to, from, next) {
  if (webviewRouteNames.includes(to.name)) {
    return next()
  }
}

function middlewareLangRedirect(to, from, next, router) {
  if (!alreadyBeforeEach && to.name !== 'auth-social') {
    alreadyBeforeEach = true

    const redirectLocale = UZBEK_CYRILLIC_ENABLED ? to.query.mirror_locale : null
    const resolvedRoute = getNewRouteByLang(to, router, redirectLocale)
    if (resolvedRoute && resolvedRoute.fullPath !== to.fullPath) {
      return next(resolvedRoute)
    }
  }
}

export function middlewarePathQueryRedirect(to, from, next, router) {
  const newPath = to.query.path || to.query.url
  if (newPath && to.name !== 'Auth' && to.name !== 'session-auth') {
    let decodedPath = newPath
    try {
      decodedPath = decodeURIComponent(newPath)
    } catch (e) {
      console.error(e)
    }

    const newRoute = router.resolve(decodedPath)
    let paramsObject = {}
    try {
      const parseUrl = new URL(`${location.origin}${decodedPath}`)
      paramsObject = Object.fromEntries(Array.from(parseUrl.searchParams))
    } catch (e) {
      console.error(e)
    }

    const params = {
      ...(newRoute?.params ?? {}),
      locale: newRoute?.params?.locale || to.params.locale,
    }
    const query = clearQueryAfterAuth({ ...to.query, ...paramsObject, ...(newRoute?.query ?? {}) })
    if (UZBEK_CYRILLIC_ENABLED) {
      delete query.mirror_locale
    }

    if (newRoute.name !== '404page') {
      return next({ name: newRoute.name, params, query, hash: newRoute.hash })
    }
  }
}

function middlewareMirrorLocaleCleanup(to, from, next) {
  if (!UZBEK_CYRILLIC_ENABLED || !to.query.mirror_locale) {
    return
  }

  const query = { ...to.query }
  delete query.mirror_locale

  return next({ path: to.path, query, hash: to.hash, replace: true })
}

function middlewareLiveChat(to) {
  const liveChatBodyClass = 'lc-hidden'
  if (to.meta.isLiveChatHidden) {
    document.body.classList.add(liveChatBodyClass)
  } else {
    document.body.classList.remove(liveChatBodyClass)
  }
}

function middlewarePostPaymentRedirect(to, from, next, router) {
  const userAuth = useUserAuthStore().getAuth
  if (!userAuth) {
    return
  }

  const prevUrlBeforePayment = localStorage.getItem('prev_url_cash_box')
  if (prevUrlBeforePayment) {
    localStorage.removeItem('prev_url_cash_box')
    try {
      const resolvedRoute = router.resolve(prevUrlBeforePayment)
      if (resolvedRoute.name !== '404page') {
        postMessageFromWebviewToApp({ type: 'payment_success', data: { type: 'payment_done' } })
        return next(resolvedRoute)
      }
    } catch (e) {
      console.error('Resolve route error', e)
    }
  }
}

function middlewareSportsBonusModal(to, from, next, router) {
  if (to.name === 'Sports' && from.name) {
    if (skipSportsBonusModalOnce) {
      skipSportsBonusModalOnce = false
      return
    }

    const { checkAndShowModal } = useBonusSportModal()
    const continueToSports = () => {
      markBonusSportModalAsHandled()
      skipSportsBonusModalOnce = true
      router.push(to.fullPath)
    }

    if (checkAndShowModal(continueToSports)) {
      return next(false)
    }
  }
}

function middlewareAuthGuard(to, from, next, router) {
  const userAuth = useUserAuthStore().getAuth
  const requireAuth = to.matched.some((record) => record.meta.auth)
  const needToSavePrevRoute = from.name && from.name !== 'GamePageNew'

  if (requireAuth) {
    if (userAuth) {
      if (needToSavePrevRoute) {
        useSystemStore().updatePrevRoute(from)
      }
      return next()
    } else {
      const userHasAccount = localStorage.getItem('hasAccount')
      const stayOnCurrentRouteOnError = from.name != null

      const tab = userHasAccount ? 'login' : 'reg'
      const locationFullPath = router.resolve(to)?.fullPath

      if (locationFullPath) {
        sessionStorage.setItem('linkAfterAuth', locationFullPath)
      }

      const config = getConfig()
      const authLinkDomain = process.env.AUTH_LINK
      const authByDeepLink = config.authDeepLink
      if (authByDeepLink || authLinkDomain) {
        const { auth } = useAuth()
        auth()
        return next(false)
      }

      if (stayOnCurrentRouteOnError) {
        const { authModal } = useVfmModals()
        authModal({ tab: tab }).open()
        return next(false)
      }

      return next({ name: 'Home', params: to.params, query: { ...to.query, modal: tab } })
    }
  } else {
    if (needToSavePrevRoute) {
      useSystemStore().updatePrevRoute({ name: from.name, params: from.params, query: from.query, meta: from.meta })
    }
    return next()
  }
}
